import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import type { Skill } from '@freelancer/datastore/collections';
import { Category } from '@freelancer/datastore/collections';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { ListItemPadding } from '@freelancer/ui/list-item';

@Component({
  standalone: false,
  selector: 'fl-skills-category',
  template: `
    <h3 class="text-body-16 SkillsHeading">
      <fl-icon
        class="SkillsShowCategories mr-xsmall desktop:hide"
        flTrackingLabel="ShowCategories"
        [name]="'ui-arrow-back'"
        [size]="IconSize.XSMALL"
        [useIconFont]="true"
        (click)="showSkillCategories()"
      ></fl-icon>

      @if (selectedCategory || searchText) {
        @if (selectedCategory && !searchText) {
          {{ selectedCategory?.name }}
        }
        @if (searchText) {
          <ng-container i18n="Search results for searched text heading">
            Search results for "{{ searchText }}"
          </ng-container>
        }
      } @else {
        <ng-container i18n="No category selected heading">
          No category selected
        </ng-container>
      }
    </h3>

    <fl-scrollable-content class="SkillsContent">
      @if (!loadingSkillSearch) {
        <fl-list
          [bottomBorder]="true"
          [clickable]="true"
          [padding]="ListItemPadding.SMALL"
        >
          @if (selectedCategory || searchText) {
            @if (filteredSkills?.length > 0) {
              @for (
                skill of filteredSkills;
                track trackBySkillId($index, skill)
              ) {
                <fl-list-item (click)="handleClick(skill)">
                  <div class="SkillsContent-details">
                    <p
                      i18n="Skill label"
                      class="SkillsContent-text"
                    >
                      {{ skill.name }}
                      @if (skill.activeProjectCount > 0) {
                        ({{ skill.activeProjectCount }} jobs)
                      }
                    </p>
                    @if (selectedSkills?.includes(skill)) {
                      <fl-icon
                        [name]="'ui-check'"
                        [color]="IconColor.SUCCESS"
                        [size]="IconSize.MID"
                        [useIconFont]="true"
                        [fillIconFont]="true"
                      ></fl-icon>
                    } @else {
                      <fl-icon
                        [name]="'ui-plus'"
                        [color]="IconColor.SECONDARY"
                        [size]="IconSize.MID"
                        [useIconFont]="true"
                        [fillIconFont]="true"
                      ></fl-icon>
                    }
                  </div>
                </fl-list-item>
              }
            } @else {
              <p
                i18n="Empty skills message"
                class="SkillsContent-message"
              >
                No skills were found
              </p>
            }
          } @else {
            <p
              i18n="No selected category message"
              class="SkillsContent-message"
            >
              Select a category to start adding skills to your profile.
            </p>
          }
        </fl-list>
      } @else {
        <fl-spinner
          class="SkillsLoading"
          flTrackingLabel="SkillsLoadingSpinner"
        ></fl-spinner>
      }
    </fl-scrollable-content>
  `,
  styleUrls: ['./skills-category.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SkillsCategoryComponent {
  IconColor = IconColor;
  IconSize = IconSize;
  ListItemPadding = ListItemPadding;

  @Input() filteredSkills?: readonly Skill[];
  @Input() includeLocalInSkillsLimit = true;
  @Input() loadingSkillSearch?: boolean;
  @Input() maxSkillsLimit?: number;
  @Input() searchText?: string;
  @Input() selectedCategory?: Category;
  @Input() selectedSkills?: readonly Skill[];

  @Output() deselectSkill = new EventEmitter<Skill>();
  @Output() selectSkill = new EventEmitter<Skill>();
  @Output() showCategories = new EventEmitter<boolean>();
  @Output() showMaxSkillsLimit = new EventEmitter();

  handleClick(skill: Skill): void {
    if (!this.selectedSkills) {
      return;
    }

    if (this.selectedSkills.includes(skill)) {
      this.deselectSkill.emit(skill);
      return;
    }

    if (
      (this.maxSkillsLimit &&
        this.selectedSkills.length < this.maxSkillsLimit) ||
      (!this.includeLocalInSkillsLimit && skill.local)
    ) {
      this.selectSkill.emit(skill);
    } else if (
      this.maxSkillsLimit &&
      this.selectedSkills.length >= this.maxSkillsLimit
    ) {
      this.showMaxSkillsLimit.emit();
    }
  }

  showSkillCategories(): void {
    this.showCategories.emit(true);
  }

  trackBySkillId(index: number, skill: Skill): number {
    return skill.id;
  }
}
